import React from 'react';
import { Lock, Phone, ShieldCheck, User } from 'lucide-react';
import { motion } from 'framer-motion';

const STEP_ORDER = ['phone', 'otp', 'prospect'];
const STEP_LABELS = ['Verify phone', 'Enter code', 'Your details'];

const UnlockPhase = ({
  unlockStep,
  unlockPhone,
  setUnlockPhone,
  handleSendOtp,
  otpSending,
  otpResendCooldownMs,
  otpCooldownSeconds,
  otpStatusMessage,
  otpCode,
  setOtpCode,
  handleVerifyOtp,
  otpChecking,
  prospectName,
  setProspectName,
  prospectEmail,
  setProspectEmail,
  prospectRelationship,
  setProspectRelationship,
  prospectOptIn,
  setProspectOptIn,
  prospectConsentAccepted,
  setProspectConsentAccepted,
  handleSubmitProspect,
  prospectSubmitting,
  unlocking,
  unlockError
}) => {
  const activeIndex = Math.max(0, STEP_ORDER.indexOf(unlockStep));

  return (
    <motion.div key="booking" className="report-stage final-report" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="report-header">
        <Lock size={40} className="check-icon" />
        <h3>Unlock your free report</h3>
      </div>

      <div className="report-summary">
        <div className="unlock-stepper" role="list" aria-label="Unlock progress">
          {STEP_LABELS.map((label, index) => {
            const state = index < activeIndex ? 'is-done' : index === activeIndex ? 'is-active' : '';
            return (
              <span
                key={label}
                role="listitem"
                className={`unlock-stepper__step ${state}`.trim()}
                aria-current={index === activeIndex ? 'step' : undefined}
              >
                Step {index + 1} — {label}
              </span>
            );
          })}
        </div>

        {unlockStep === 'phone' && (
          <div className="unlock-step-card">
            <div className="unlock-step-header">
              <span className="unlock-step-badge">Step 1 of 3</span>
              <p><Phone size={18} className="check-icon" /> Verify your phone to continue.</p>
            </div>
            <p className="unlock-step-subcopy">We’ll send a Versa Launch verification code to secure your report access.</p>
            <div className="unlock-field">
              <label className="booking-note" htmlFor="unlock-phone">Phone number</label>
              <input
                id="unlock-phone"
                type="tel"
                className="search-field"
                placeholder="(555) 555-5555"
                value={unlockPhone}
                onChange={(event) => setUnlockPhone(event.target.value)}
              />
            </div>
            <div className="booking-gate-actions">
              <button className="btn-try-again" type="button" onClick={handleSendOtp} disabled={otpSending || otpResendCooldownMs > 0}>
                {otpSending ? 'Sending code…' : otpResendCooldownMs > 0 ? `Retry in ${otpCooldownSeconds}s` : 'Send verification code'}
              </button>
            </div>
          </div>
        )}

        {unlockStep === 'otp' && (
          <div className="unlock-step-card">
            <div className="unlock-step-header">
              <span className="unlock-step-badge">Step 2 of 3</span>
              <p><ShieldCheck size={18} className="check-icon" /> Enter your Versa Launch verification code.</p>
            </div>
            {otpStatusMessage && <p className="booking-note booking-status-success">{otpStatusMessage}</p>}
            <div className="unlock-field">
              <label className="booking-note" htmlFor="unlock-otp">Verification code</label>
              <input
                id="unlock-otp"
                type="text"
                className="search-field"
                placeholder="Enter code"
                value={otpCode}
                onChange={(event) => setOtpCode(event.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
              />
            </div>
            <div className="booking-gate-actions">
              <button className="btn-try-again" type="button" onClick={handleVerifyOtp} disabled={otpChecking}>
                {otpChecking ? 'Verifying…' : 'Verify code'}
              </button>
              <button className="btn-try-again secondary" type="button" onClick={handleSendOtp} disabled={otpSending || otpResendCooldownMs > 0}>
                {otpSending ? 'Resending…' : otpResendCooldownMs > 0 ? `Resend in ${otpCooldownSeconds}s` : 'Resend code'}
              </button>
            </div>
          </div>
        )}

        {unlockStep === 'prospect' && (
          <div className="unlock-step-card">
            <div className="unlock-step-header">
              <span className="unlock-step-badge">Step 3 of 3</span>
              <p><User size={18} className="check-icon" /> Tell us about you to unlock the full report.</p>
            </div>

            <div className="prospect-use-note" role="note" aria-label="How your info is used">
              <strong>How Versa Launch uses this information</strong>
              <ul>
                <li>Verify you’re connected to the business report.</li>
                <li>Personalize recommended actions in your unlocked report.</li>
                <li>Send optional follow-ups only if you opt in.</li>
              </ul>
            </div>

            <div className="unlock-form-grid">
              <div className="unlock-field">
                <label className="booking-note" htmlFor="prospect-name">Name</label>
                <input
                  id="prospect-name"
                  type="text"
                  className="search-field"
                  placeholder="Your name"
                  value={prospectName}
                  onChange={(event) => setProspectName(event.target.value)}
                />
              </div>

              <div className="unlock-field">
                <label className="booking-note" htmlFor="prospect-email">Email</label>
                <input
                  id="prospect-email"
                  type="email"
                  className="search-field"
                  placeholder="Your email"
                  value={prospectEmail}
                  onChange={(event) => setProspectEmail(event.target.value)}
                />
              </div>

              <div className="unlock-field">
                <label className="booking-note" htmlFor="prospect-relationship">Relationship</label>
                <select
                  id="prospect-relationship"
                  className="search-field"
                  value={prospectRelationship}
                  onChange={(event) => setProspectRelationship(event.target.value)}
                >
                  <option value="owner_manager">I'm the business owner or manager</option>
                  <option value="service_provider">I provide services to this business</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="consent-panel" aria-label="Consent options">
              <label className="consent-row optional">
                <input
                  type="checkbox"
                  checked={prospectOptIn}
                  onChange={(event) => setProspectOptIn(event.target.checked)}
                />
                <span>Receive optional updates on score changes and competitors</span>
              </label>

              <label className="consent-row required">
                <input
                  type="checkbox"
                  checked={prospectConsentAccepted}
                  onChange={(event) => setProspectConsentAccepted(event.target.checked)}
                />
                <span>
                  I agree to the Terms of Service and Privacy Policy, and consent to data collection and use to unlock this report.
                </span>
              </label>
            </div>

            <div className="booking-gate-actions">
              <button className="btn-try-again" type="button" onClick={handleSubmitProspect} disabled={prospectSubmitting || unlocking}>
                {prospectSubmitting || unlocking ? 'Unlocking…' : 'Unlock report'}
              </button>
            </div>
          </div>
        )}

        {unlockError && <p className="booking-note booking-error">{unlockError}</p>}
        <p className="booking-note">Unlock verifies your scan session and returns full competitor ranking + AI recommendations.</p>
      </div>
    </motion.div>
  );
};

export default UnlockPhase;
